// src/stores/gamificationStore.js - Zustand Gamification Store for Relationship Compass
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { achievements, pointValues, getLevelInfo } from '../data/gamification';
import { triggerQuickCelebration } from '../components/shared/CelebrationAnimation';

const GAMIFICATION_VERSION = '1.0';

// Points, levels and achievements are saved on this device (the persist
// middleware below, under relationship-compass-gamification). With cloud
// backup on, services/hubSync.js copies that saved state to the person's DDD
// account in the Auth Hub. The store itself never writes to Firebase.

// Create the Zustand store
export const useGamificationStore = create(
  devtools(
    persist(
      (set, get) => ({
        // State
        totalPoints: 0,
        currentLevel: 1,
        completedModules: [],
        completedTools: [],
        unlockedAchievements: [],
        pendingAchievements: [],
        currentStreak: 0,
        longestStreak: 0,
        lastActivity: null,
        isInitialized: false,
        completedWeeklyChallenges: [],

        // Computed values (getters)
        getCurrentLevelData: () => {
          const points = get().totalPoints;
          return getLevelInfo(points);
        },

        getPointsToNextLevel: () => {
          const levelInfo = get().getCurrentLevelData();
          return levelInfo.pointsToNextLevel;
        },

        // Actions
        addPoints: (amount) => {
          const state = get();
          const newPoints = state.totalPoints + amount;
          const oldLevelInfo = getLevelInfo(state.totalPoints);
          const newLevelInfo = getLevelInfo(newPoints);
          const leveledUp = newLevelInfo.currentLevel.level > oldLevelInfo.currentLevel.level;

          set({
            totalPoints: newPoints,
            currentLevel: newLevelInfo.currentLevel.level,
            lastActivity: new Date().toISOString(),
          });

          // Trigger celebration
          if (leveledUp) {
            const newLevel = newLevelInfo.currentLevel;
            triggerQuickCelebration(`Level ${newLevel.level}! ${newLevel.title}`);
          }
        },

        completeModule: (moduleId) => {
          const state = get();

          // Don't complete twice
          if (state.completedModules.includes(moduleId)) {
            return;
          }

          const newCompleted = [...state.completedModules, moduleId];

          set({
            completedModules: newCompleted,
            lastActivity: new Date().toISOString(),
          });

          // Award points
          get().addPoints(pointValues.COMPLETE_MODULE, `Completed Module ${moduleId}`);

          // Check for achievements
          get().checkModuleAchievements(newCompleted);
        },

        unlockAchievement: (achievementId) => {
          const state = get();

          if (state.unlockedAchievements.includes(achievementId)) {
            return;
          }

          const achievement = achievements[achievementId];
          if (!achievement) return;

          set({
            unlockedAchievements: [...state.unlockedAchievements, achievementId],
            pendingAchievements: [...state.pendingAchievements, achievementId],
          });

          // Award achievement points
          if (achievement.points) {
            get().addPoints(achievement.points, `Unlocked: ${achievement.name}`);
          }

          // Celebration
          triggerQuickCelebration(`${achievement.icon} ${achievement.name}`);
        },

        updateStreak: () => {
          const state = get();
          const now = new Date();
          const lastActivity = state.lastActivity ? new Date(state.lastActivity) : null;

          if (!lastActivity) {
            // First activity
            set({
              currentStreak: 1,
              longestStreak: 1,
              lastActivity: now.toISOString(),
            });
            get().addPoints(pointValues.DAILY_LOGIN, 'Daily login');
            return;
          }

          const daysSinceLastActivity = Math.floor(
            (now - lastActivity) / (1000 * 60 * 60 * 24)
          );

          let newStreak = state.currentStreak;

          if (daysSinceLastActivity === 0) {
            // Same day - no change
            return;
          } else if (daysSinceLastActivity === 1) {
            // Consecutive day - increment
            newStreak = state.currentStreak + 1;
            get().addPoints(pointValues.DAILY_LOGIN, 'Daily login streak');

            // Check streak achievements
            if (newStreak === 7) {
              get().unlockAchievement('WEEK_WARRIOR');
              get().addPoints(pointValues.STREAK_7_DAYS, '7-day streak');
            } else if (newStreak === 30) {
              get().unlockAchievement('MONTH_MASTER');
              get().addPoints(pointValues.STREAK_30_DAYS, '30-day streak');
            } else if (newStreak === 100) {
              get().unlockAchievement('CENTURION');
              get().addPoints(pointValues.STREAK_100_DAYS, '100-day streak');
            }
          } else {
            // Streak broken - reset
            newStreak = 1;
            get().addPoints(pointValues.DAILY_LOGIN, 'Daily login');
          }

          const newLongestStreak = Math.max(newStreak, state.longestStreak);

          set({
            currentStreak: newStreak,
            longestStreak: newLongestStreak,
            lastActivity: now.toISOString(),
          });
        },

        clearPendingAchievements: () => {
          set({ pendingAchievements: [] });
        },

        checkModuleAchievements: (completedModules) => {
          // Check for milestone achievements
          if (completedModules.length === 1) {
            get().unlockAchievement('FIRST_STEPS');
          }
          if (completedModules.length >= 8) {
            get().unlockAchievement('MODULE_MASTER');
            get().addPoints(pointValues.FINISH_ALL_MODULES, 'Completed all modules');
          }
        },

        checkToolAchievements: (completedTools) => {
          // Check for tool milestone achievements
          if (completedTools.length === 1) {
            get().unlockAchievement('TOOL_EXPLORER');
          }
          if (completedTools.length >= 8) {
            get().unlockAchievement('TOOL_MASTER');
            get().addPoints(pointValues.USE_ALL_TOOLS, 'Used all tools');
          }
        },

        completeTool: (toolId) => {
          const state = get();

          // Don't complete twice
          if (state.completedTools.includes(toolId)) {
            return;
          }

          const newCompleted = [...state.completedTools, toolId];

          set({
            completedTools: newCompleted,
            lastActivity: new Date().toISOString(),
          });

          // Award points
          get().addPoints(pointValues.FIRST_TOOL_USE, `Completed Tool ${toolId}`);

          // Check for achievements
          get().checkToolAchievements(newCompleted);
        },

        useToolSession: (toolId) => {
          get().addPoints(pointValues.TOOL_SESSION, `Used ${toolId}`);
        },

        completeWeeklyChallenge: (challengeId, difficulty) => {
          const state = get();

          // Don't complete twice
          if (state.completedWeeklyChallenges.includes(challengeId)) {
            return;
          }

          const newCompleted = [...state.completedWeeklyChallenges, challengeId];

          set({
            completedWeeklyChallenges: newCompleted,
            lastActivity: new Date().toISOString(),
          });

          // Award points based on difficulty
          const points = difficulty === 'Beginner' ? pointValues.WEEKLY_CHALLENGE_BEGINNER :
                         difficulty === 'Intermediate' ? pointValues.WEEKLY_CHALLENGE_INTERMEDIATE :
                         pointValues.WEEKLY_CHALLENGE_ADVANCED;

          get().addPoints(points, `Completed ${difficulty} challenge`);

          // Check challenge achievements
          if (newCompleted.length === 1) {
            get().unlockAchievement('CHALLENGE_ACCEPTED');
          } else if (newCompleted.length === 10) {
            get().unlockAchievement('CHALLENGE_CRUSHER');
            get().addPoints(pointValues.WEEKLY_CHALLENGES_10, '10 challenges completed');
          } else if (newCompleted.length === 25) {
            get().unlockAchievement('CHALLENGE_LEGEND');
          }
        },

        completeAssessment: () => {
          get().addPoints(pointValues.COMPLETE_ASSESSMENT, 'Completed assessment');
          get().unlockAchievement('ASSESSMENT_COMPLETE');
        },

        retakeAssessment: () => {
          get().addPoints(pointValues.RETAKE_ASSESSMENT, 'Retook assessment');
        },

        // Start-up: saved state is already loaded from this device by the
        // persist middleware; just bring the streak up to date.
        initialize: () => {
          set({ isInitialized: true });
          get().updateStreak();
        },

        // Clear all data (for logout)
        clearUserData: () => {
          set({
            totalPoints: 0,
            currentLevel: 1,
            completedModules: [],
            completedTools: [],
            unlockedAchievements: [],
            pendingAchievements: [],
            currentStreak: 0,
            longestStreak: 0,
            lastActivity: null,
            completedWeeklyChallenges: [],
          });
        },
      }),
      {
        name: 'relationship-compass-gamification',
        partialize: (state) => ({
          totalPoints: state.totalPoints,
          currentLevel: state.currentLevel,
          completedModules: state.completedModules,
          completedTools: state.completedTools,
          unlockedAchievements: state.unlockedAchievements,
          currentStreak: state.currentStreak,
          longestStreak: state.longestStreak,
          lastActivity: state.lastActivity,
          completedWeeklyChallenges: state.completedWeeklyChallenges,
        }),
      }
    ),
    { name: 'GamificationStore' }
  )
);

// Hook to initialize on app start
export const initializeGamification = () => {
  useGamificationStore.getState().initialize();
};

// Export for use
export const useGamification = useGamificationStore;
